// useAppPermissions.js — Layout(AdminLayout) 전용 권한 데이터 fetch + usePermissions wrapping
//
// 사용처: AdminLayout (admin/employee 공용 layout) — 사이드바 메뉴 노출 분기에 사용
// 데이터 소스: /api/me, /api/my/work-feature-assignments, /api/my/permissions
//
// 출력: { permissions, me, loaded }
//   - permissions: usePermissions 반환 (canUseMyAttendance, canUseLeaveManagement 등 다수 boolean)
//   - me: 사용자 정보 (role/name/department/workerType/externalCompany)
//   - loaded: 3개 fetch 모두 끝났는지 여부 (사이드바 깜빡임 방지)
//
// 작성: 2026-04-27, dashboard-layout-migration Step 3
import { useEffect, useState } from 'react';
import { apiClient } from '../api/apiClient';
import { usePermissions } from './usePermissions';

export function useAppPermissions() {
  const [me, setMe] = useState(null);
  const [permCodesSet, setPermCodesSet] = useState(null); // Set<string>
  const [taskCodes, setTaskCodes] = useState([]);
  const [featureSetNames, setFeatureSetNames] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      const [meRes, taskRes, permRes] = await Promise.all([
        apiClient.get('/api/me'),
        apiClient.get('/api/my/work-feature-assignments'),
        apiClient.get('/api/my/permissions'),
      ]);
      if (!active) return;
      if (meRes.ok) setMe(meRes.data?.user || null);
      if (taskRes.ok) {
        setTaskCodes(taskRes.data?.taskCodes || []);
        setFeatureSetNames(taskRes.data?.featureSetNames || []);
      }
      if (permRes.ok && Array.isArray(permRes.data?.permCodes)) {
        setPermCodesSet(new Set(permRes.data.permCodes));
      }
      setLoaded(true);
    })();
    return () => { active = false; };
  }, []);

  const permissions = usePermissions({
    permCodes: permCodesSet,
    taskCodes,
    featureSetNames,
    workerType: me?.workerType,
    externalCompany: me?.externalCompany,
    role: me?.role, // 2026-04-29: admin 미팅지도 노출 조건용
    callCompanyCount: me?.callCompanyCount, // 2026-10-14: A '(통합)' 첫 화면 후보 판정
  });

  // permCodes Set 도 같이 노출 — SidebarContent 권한 기반 필터링용 (2026-04-27 옵션 3)
  // admin role 사용자는 백엔드 getUserPermCodes 의 admin 분기로 64개 권한 자동 보유
  return { permissions, me, loaded, permCodes: permCodesSet };
}
